import React, { forwardRef, useImperativeHandle, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Node, Mark, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Code2 } from 'lucide-react';
import { useTheme } from '../ThemeContext';

const Div = Node.create({
  name: 'div',
  group: 'block',
  content: 'block+',
  parseHTML() {
    return [{ tag: 'div' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes), 0];
  },
  addAttributes() {
    return {
      style: {
        default: null,
        parseHTML: element => element.getAttribute('style'),
        renderHTML: attributes => {
          if (!attributes.style) return {};
          return { style: attributes.style };
        },
      },
      class: {
        default: null,
        parseHTML: element => element.getAttribute('class'),
        renderHTML: attributes => {
          if (!attributes.class) return {};
          return { class: attributes.class };
        },
      },
    };
  },
});

const Span = Mark.create({
  name: 'span',
  parseHTML() {
    return [{ tag: 'span' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes), 0];
  },
  addAttributes() {
    return {
      style: {
        default: null,
        parseHTML: element => element.getAttribute('style'),
        renderHTML: attributes => {
          if (!attributes.style) return {};
          return { style: attributes.style };
        },
      },
      class: {
        default: null,
        parseHTML: element => element.getAttribute('class'),
        renderHTML: attributes => {
          if (!attributes.class) return {};
          return { class: attributes.class };
        },
      },
    };
  },
});

const QuillEditor = forwardRef(function QuillEditor({ content, setContent }, ref) {
  const { t, isDark } = useTheme();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: true,
        blockquote: true,
      }),
      Image.configure({
        allowBase64: true,
        inline: false,
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableCell,
      TableHeader,
      Underline,
      Link.configure({
        openOnClick: false,
      }),
      Placeholder.configure({
        placeholder: 'Tell your story...',
      }),
      Div,
      Span,
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      setContent(editor.getHTML());
    },
  });

  useImperativeHandle(ref, () => ({
    getEditor: () => editor,
    setContent: (html) => {
      if (editor) {
        editor.commands.setContent(html, false);
      }
    },
    setRawHTML: (html) => {
      if (!editor) return;
      editor.commands.setContent(html, false, {
        preserveWhitespace: 'full',
      });
    },
    debugHTML: () => editor ? editor.getHTML() : null,
  }));

  const toggleLink = useCallback(() => {
    if (!editor) return;
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run();
    } else {
      const url = window.prompt('Enter URL:');
      if (url) {
        editor.chain().focus().setLink({ href: url }).run();
      }
    }
  }, [editor]);

  if (!editor) return null;

  const ToolbarButton = ({ onClick, active, children, title }) => (
    <button
      onClick={onClick}
      title={title}
      style={{
        width: '28px',
        height: '28px',
        padding: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: active ? (isDark ? 'rgba(212,165,116,0.15)' : 'rgba(61,36,18,0.08)') : 'transparent',
        border: 'none',
        borderRadius: '4px',
        color: active ? t.accent : '#78716c',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        fontSize: '14px',
        fontWeight: active ? 700 : 600,
        fontFamily: 'Poppins, sans-serif',
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.color = t.accent; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.color = '#78716c'; }}
    >
      {children}
    </button>
  );

  const Separator = () => (
    <div style={{
      width: '1px',
      height: '20px',
      background: t.borderStrong,
      margin: '0 6px',
    }} />
  );

  return (
    <div style={{ position: 'relative', marginBottom: '80px' }}>
      <EditorContent editor={editor} />

      {/* Floating Toolbar */}
      <div style={{
        border: `1px solid ${t.borderStrong}`,
        borderRadius: '12px',
        padding: '8px 16px',
        background: t.surface,
        position: 'fixed',
        bottom: '24px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        display: 'flex',
        gap: '4px',
        alignItems: 'center',
      }}>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          title="Bold"
        >B</ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          title="Italic"
        ><em>I</em></ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')}
          title="Underline"
        ><u>U</u></ToolbarButton>

        <Separator />

        <ToolbarButton
          onClick={toggleLink}
          active={editor.isActive('link')}
          title="Link"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
          </svg>
        </ToolbarButton>

        <Separator />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          title="Ordered List"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/>
            <path d="M4 6h1v4"/><path d="M4 10h2"/><path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"/>
          </svg>
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          title="Bullet List"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="9" y1="6" x2="20" y2="6"/><line x1="9" y1="12" x2="20" y2="12"/><line x1="9" y1="18" x2="20" y2="18"/>
            <circle cx="4" cy="6" r="1.5" fill="currentColor"/><circle cx="4" cy="12" r="1.5" fill="currentColor"/><circle cx="4" cy="18" r="1.5" fill="currentColor"/>
          </svg>
        </ToolbarButton>

        <Separator />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive('blockquote')}
          title="Blockquote"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V21z"/>
            <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3z"/>
          </svg>
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          active={editor.isActive('codeBlock')}
          title="Code Block"
        >
          <Code2 size={15} />
        </ToolbarButton>

        <Separator />

        <ToolbarButton
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
          title="Insert Table"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/>
          </svg>
        </ToolbarButton>

        <Separator />

        <ToolbarButton
          onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
          title="Clear Formatting"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 7V4h16v3"/><path d="M9 20h6"/><path d="M12 4v16"/>
            <line x1="3" y1="21" x2="21" y2="3" stroke="currentColor" strokeWidth="1.5"/>
          </svg>
        </ToolbarButton>
      </div>

      <style>{`
        /* TipTap Editor Content */
        .tiptap {
          font-family: 'Poppins', sans-serif;
          font-size: 1.125rem;
          line-height: 1.8;
          color: ${t.text};
          min-height: 400px;
          padding: 12px 0;
          outline: none;
        }

        .tiptap p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: ${t.textTertiary};
          pointer-events: none;
          height: 0;
          font-family: 'Poppins', sans-serif;
          font-style: normal;
        }

        /* Links */
        .tiptap a {
          color: #2563eb;
          text-decoration: underline;
        }

        /* Bold, Italic, Underline */
        .tiptap strong {
          font-weight: 600;
          color: ${t.text};
        }

        .tiptap em {
          font-style: italic;
        }

        .tiptap u {
          text-decoration: underline;
        }

        /* Lists */
        .tiptap ul,
        .tiptap ol {
          padding-left: 1.5rem;
          margin-bottom: 1rem;
        }

        .tiptap ul {
          list-style-type: disc;
        }

        .tiptap ul ul {
          list-style-type: circle;
          margin-top: 0.25rem;
          margin-bottom: 0.25rem;
        }

        .tiptap ul ul ul {
          list-style-type: square;
        }

        .tiptap ol {
          list-style-type: decimal;
        }

        .tiptap ol ol {
          list-style-type: lower-alpha;
          margin-top: 0.25rem;
          margin-bottom: 0.25rem;
        }

        .tiptap ol ol ol {
          list-style-type: lower-roman;
        }

        .tiptap li {
          margin-bottom: 0.25rem;
          padding-left: 0.5rem;
        }

        /* Paragraphs */
        .tiptap p {
          margin-bottom: 1rem;
        }

        /* Blockquotes */
        .tiptap blockquote {
          border-left: 4px solid ${t.accent};
          padding-left: 1rem;
          margin-left: 0;
          margin-bottom: 1rem;
          color: #78716c;
          font-style: italic;
        }

        /* Images */
        .tiptap img {
          max-width: 100%;
          height: auto;
        }

        /* Tables */
        .tiptap table {
          border-collapse: collapse;
          table-layout: fixed;
          width: 100%;
          margin-bottom: 1rem;
          overflow: hidden;
        }

        .tiptap td,
        .tiptap th {
          min-width: 1em;
          border: 1px solid ${t.borderStrong};
          padding: 8px 12px;
          vertical-align: top;
          box-sizing: border-box;
          position: relative;
        }

        .tiptap th {
          font-weight: 600;
          text-align: left;
          background-color: ${isDark ? 'rgba(245,230,200,0.06)' : 'rgba(61,36,18,0.03)'};
        }

        /* Code blocks */
        .tiptap pre {
          background: ${isDark ? 'rgba(245,230,200,0.06)' : 'rgba(61,36,18,0.03)'};
          border-radius: 6px;
          padding: 12px 16px;
          margin-bottom: 1rem;
          overflow-x: auto;
        }

        .tiptap pre code {
          font-family: 'SFMono-Regular', Consolas, monospace;
          font-size: 0.875rem;
          color: ${t.text};
        }

        .tiptap code {
          font-family: 'SFMono-Regular', Consolas, monospace;
          font-size: 0.875em;
          background: ${isDark ? 'rgba(245,230,200,0.08)' : 'rgba(61,36,18,0.05)'};
          border-radius: 3px;
          padding: 0.2em 0.4em;
        }

        /* Styled divs (imported HTML cards/containers) */
        .tiptap div[style] {
          display: block;
          margin: 12px 0;
        }

        .tiptap div {
          display: block;
        }

        /* Styled spans (imported inline styles) */
        .tiptap span[style] {
          display: inline;
        }
      `}</style>
    </div>
  );
});

export default QuillEditor;
